import { initWMUKID, initCDPID } from './identity';
import { cookie } from '@turnercode/cdp-cookie';
import { sendRequest } from '../utils/sendRequest';
import { URLS, CCPA_LOCATIONS, EMEA_LOCATIONS } from '../utils/constants';
import {
  getAttUuid,
  getCnnUid,
  getConvivaId,
  getEcid,
  getKruxId,
  getOptanonConsentCookie,
  getZionId,
  hydratePayload,
} from '../utils/hydratePayload';
import { debug } from '../utils/logger';
import { fromQueryString, isBotUserAgent } from '../utils/browser';
import { getTCFConsentString, hasLiveRampTCFConsent } from './privacy';
import {
  CdpidValue,
  PayloadCore,
  AuthTokenResolveRequest,
  AuthTokenResolveResponse,
  AuthServiceTokenObject,
  ConsentProperties,
} from '../interfaces';

const parseCookie = (str) =>
  str
    .split(';')
    .map((v) => v.split('='))
    .reduce((acc, v) => {
      acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
      return acc;
    }, {});

export const coreDependencies = (psm) => {
  const global = window as any;
  global.WM = global.WM || {};
  global.WM.PSM = global.WM.PSM || {};
  const psmObj = global.WM.PSM;

  const deps = {
    psmObject: psmObj,
    initWMUKID: initWMUKID,
    initCDPID: initCDPID,
    getWMUKID: (inIframe) => {
      if (inIframe) {
        return (parseCookie(document.cookie)['WMUKID_STABLE'] as string) || 'Unknown';
      }
      return (cookie.get('WMUKID_STABLE') as string) || 'Unknown';
    },
    getCDPID: () => {
      const data = cookie.get('CDPID') as CdpidValue;

      if (data && typeof data === 'object' && 'cdpId' in data && 'wmukId' in data) {
        return data.cdpId;
      }
      return (data as string) || 'Unknown';
    },
    getConsentProperties: (): ConsentProperties => {
      return {
        uspString: cookie.get('usprivacy') as string,
        consentRule: psm.consentRule,
        optanonConsent: getOptanonConsentCookie() || '',
        psmPrivacyMode: psm.coppaPrivacyMode,
      };
    },
    resolveAuthTokens: async (core: PayloadCore, cookie: any, psm: any) => {
      // Crawlers never become an addressable audience, so resolving tokens for them
      // only spends token-service calls and skews the token metrics. Bail before any
      // of that work, and before the 'token updated' event this would emit — the
      // request queue already drops bot traffic, but only at the send step.
      if (isBotUserAgent()) {
        return false;
      }

      if (!psm.queryFlag('auth-service')) {
        debug({
          message: '[PSM]: auth-service flag is disabled',
          methodName: 'resolveAuthTokens',
          eventType: 'authService',
        });
        return false;
      }

      const uspString = cookie.get('usprivacy') as string;
      const countryCode = psm.countryCode as string;
      const isDomestic = CCPA_LOCATIONS.includes(countryCode);
      const isInternational = !isDomestic;
      const isEMEA = EMEA_LOCATIONS.includes(countryCode);

      if (!isInternational && !uspString) {
        return false;
      }

      // Waterfall priority: lower number = higher priority
      const PRIORITY = { cnnAuth: 1, newsletter: 2, display: 3, uatsWmukid: 4, uatsIp: 5 };
      const ALL_PRIORITIES = [1, 2, 3, 4, 5];

      type TokenMeta = { p: number };

      /**
       * Whether an incoming token should take over the live cookie. Lower number =
       * higher priority.
       *
       * Equal priority wins too, so a tier that re-resolves replaces its own token
       * with the fresher one. Requiring a strict improvement meant the live cookie
       * kept a value the tier had already superseded until the old one expired, which
       * both shortened the usable life of the token and let a token the provider had
       * rotated stay in circulation.
       */
      const shouldWriteToken = (cookieName: string, metaCookieName: string, incomingPriority: number): boolean => {
        const currentToken = cookie.get(cookieName) as string | null;
        if (!currentToken) return true;
        const meta = cookie.get(metaCookieName) as TokenMeta | null;
        if (!meta || typeof meta.p !== 'number') return true;
        return incomingPriority <= meta.p;
      };

      const writeTokenWithPriority = (
        cookieName: string,
        metaCookieName: string,
        tokenValue: string,
        expiration: string,
        priority: number,
      ) => {
        const expires = new Date(expiration);
        cookie.set(`psm_${cookieName}_t${priority}`, tokenValue, { expires });
        if (shouldWriteToken(cookieName, metaCookieName, priority)) {
          cookie.set(cookieName, tokenValue, { expires });
          cookie.set(metaCookieName, { p: priority }, { expires });
        }
      };

      const promoteBackupToken = (cookieName: string, metaCookieName: string) => {
        if (cookie.get(cookieName)) return;
        for (const p of ALL_PRIORITIES) {
          const backup = cookie.get(`psm_${cookieName}_t${p}`) as string | null;
          if (backup) {
            cookie.set(cookieName, backup);
            cookie.set(metaCookieName, { p });
            return;
          }
        }
      };

      promoteBackupToken('idl_env', 'psm_idl_env_meta');
      promoteBackupToken('glp_ggw2', 'psm_glp_ggw2_meta');
      promoteBackupToken('pairId', 'psm_pairId_meta');

      const isWithin24Hours = (timestampCookieName: string): boolean => {
        const ts = cookie.get(timestampCookieName) as string | null;
        if (!ts) return false;
        const lifespan = (Date.now() - new Date(ts).getTime()) / 1000 / 60 / 60;
        return lifespan < 24;
      };

      const ip = psm.location?.ip_address || '';
      const tcfConsent = isInternational ? getTCFConsentString() : null;

      const buildConsentProperties = (): { usp: string; ct?: string; cv?: string } => {
        const consent: { usp: string; ct?: string; cv?: string } = {
          usp: uspString || '1---',
        };
        if (isInternational && tcfConsent) {
          consent.ct = '4';
          consent.cv = tcfConsent;
        }
        return consent;
      };

      const buildBaseRequest = (
        authObjects: { provider: string; details: { id: string } }[],
      ): AuthTokenResolveRequest => {
        const req: AuthTokenResolveRequest = {
          wmukid: psm.getWMUKID(),
          brand: psm.config.brand,
          ids: {
            attuuid: getAttUuid(),
            cdpid: psm.getCDPID(),
            convivaid: getConvivaId(),
            ecid: getEcid(),
            kruxid: getKruxId(),
            wminid: cookie.get('wminid') as string,
            zionid: getZionId(),
            cnnuid: getCnnUid(),
          },
          location: psm.getLocationProperties(),
          consentProperties: buildConsentProperties(),
          authentication: authObjects,
        };
        if (isInternational && ip) {
          req.ip = ip;
        }
        return req;
      };

      const processTokenResponse = (tokens: AuthServiceTokenObject[], priority: number) => {
        const lrToken = tokens.find((t) => t.provider === 'liveramp');
        if (lrToken?.token) {
          writeTokenWithPriority('idl_env', 'psm_idl_env_meta', lrToken.token, lrToken.expiration, priority);
        }
        const tdToken = tokens.find((t) => t.provider === 'tradedesk');
        if (tdToken?.token && !isInternational) {
          writeTokenWithPriority('glp_ggw2', 'psm_glp_ggw2_meta', tdToken.token, tdToken.expiration, priority);
        }
        const pairToken = tokens.find((t) => t.provider === 'pairId');
        if (pairToken?.token) {
          writeTokenWithPriority('pairId', 'psm_pairId_meta', pairToken.token, pairToken.expiration, priority);
        }
      };

      const sendTierRequest = async (
        authObjects: { provider: string; details: { id: string } }[],
        timestampCookie: string,
        priority: number,
        tierName: string,
      ): Promise<void> => {
        const requestBody = buildBaseRequest(authObjects);
        try {
          const response: AuthTokenResolveResponse = await sendRequest(URLS.authSvc[psm.config.psmEnvironment], {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
          });
          processTokenResponse(response.tokens, priority);
          cookie.set(timestampCookie, new Date());
        } catch (err) {
          debug({ err, eventType: 'authService', methodName: `resolveAuthTokens (${tierName})` });
          cookie.set(timestampCookie, new Date());
        }
      };

      let anyRequestSent = false;

      // --- Tier 1: CNN Auth Sign-in ---
      const cnnUid = getCnnUid();
      if (cnnUid && !isWithin24Hours('resolveCNNAuthTimestamp')) {
        let shouldSendCnnAuth = true;
        if (isEMEA) {
          shouldSendCnnAuth = await hasLiveRampTCFConsent();
          if (!shouldSendCnnAuth) {
            debug({
              message: '[PSM]: EMEA CNN Auth skipped — no LiveRamp TCF consent (vendor 97 / purpose 1)',
              methodName: 'resolveAuthTokens',
              eventType: 'authService',
            });
          }
        }
        if (shouldSendCnnAuth) {
          await sendTierRequest(
            [{ provider: 'cnnAuthentication', details: { id: cnnUid } }],
            'resolveCNNAuthTimestamp',
            PRIORITY.cnnAuth,
            'cnnAuth',
          );
          anyRequestSent = true;
        }
      }

      // --- Tier 2: Newsletter click (Zeta / Braze) ---
      if (!isWithin24Hours('resolveNewsletterTimestamp')) {
        const newsletterAuthObjects: { provider: string; details: { id: string } }[] = [];

        const bt_ts = fromQueryString('bt_ts', window.location.href);
        const utm_term = fromQueryString('utm_term', window.location.href);
        if (bt_ts && utm_term) {
          const btLifespan = (Date.now() - parseInt(bt_ts)) / 1000 / 60 / 60;
          if (btLifespan <= 24) {
            newsletterAuthObjects.push({ provider: 'zeta', details: { id: utm_term } });
          }
        }

        const primo = fromQueryString('primo', window.location.href);
        if (primo) {
          newsletterAuthObjects.push({ provider: 'braze', details: { id: primo } });
        }

        if (newsletterAuthObjects.length > 0) {
          await sendTierRequest(newsletterAuthObjects, 'resolveNewsletterTimestamp', PRIORITY.newsletter, 'newsletter');
          anyRequestSent = true;
        }
      }

      // --- Tier 3: IAL / Display (WMUKID) ---
      if (!isWithin24Hours('resolveDisplayTimestamp')) {
        await sendTierRequest(
          [{ provider: 'wmukid', details: { id: psm.getWMUKID() } }],
          'resolveDisplayTimestamp',
          PRIORITY.display,
          'display',
        );
        anyRequestSent = true;
      }

      // --- Tier 4: UATS WMUKID (domestic/CCPA only) ---
      if (!isInternational && !isWithin24Hours('resolveUATSWMUKIDTimestamp')) {
        await sendTierRequest(
          [{ provider: 'uatsWmukid', details: { id: psm.getWMUKID() } }],
          'resolveUATSWMUKIDTimestamp',
          PRIORITY.uatsWmukid,
          'uatsWmukid',
        );
        anyRequestSent = true;
      }

      // --- Tier 5: UATS IP (domestic/CCPA only) ---
      if (!isInternational && ip && !isWithin24Hours('resolveUATSIPTimestamp')) {
        await sendTierRequest(
          [{ provider: 'uatsIp', details: { id: ip } }],
          'resolveUATSIPTimestamp',
          PRIORITY.uatsIp,
          'uatsIp',
        );
        anyRequestSent = true;
      }

      if (anyRequestSent) {
        psm.setSessionProperties();
        hydratePayload(psm, core);
        core.trackTokenEvent('token updated', new Date().toISOString(), (data) => {
          if (psm.queryFlag('doppler-send-token-event')) {
            psm.requestQueue.addItem(data);
            psm.resetNewSessionFields();
          }
        });
      }

      return anyRequestSent;
    },
    platform: window.navigator.platform,
  };

  // Run the waterfall one at a time. The CNN sign-in watcher can call this while the
  // run started at init is still awaiting the token service, and each tier is only
  // throttled by a timestamp cookie that the earlier run writes when that tier
  // finishes — so overlapping runs would re-send tiers the first run had not reached
  // yet. Queueing instead of dropping matters: the second call is the one carrying the
  // newly available CNN uid, so it still has to run.
  const runWaterfall = deps.resolveAuthTokens;
  let waterfallQueue: Promise<unknown> = Promise.resolve();
  deps.resolveAuthTokens = (core: PayloadCore, cookie: any, psm: any): Promise<boolean> => {
    const run = waterfallQueue.then(() => runWaterfall(core, cookie, psm));
    waterfallQueue = run.catch(() => undefined);
    return run;
  };

  return deps;
};
